import { FC, useMemo } from 'react'
import { QueryStatus } from '@tanstack/react-query'

import { copyToClipboard } from '@revolut-internal/ui-kit'

import { useToasts } from 'common/toasts/ToastsProvider'
import { LimitedTable } from 'modules/Monitoring/components/LimitedTable/LimitedTable'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import { useTableState } from 'modules/Monitoring/hooks/useTableState'
import {
  IncidentTimelineSearchRequest,
  QueueHealthMetricsOverview,
  ResourcesSideParams,
  SpecialisationHealth,
} from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { deriveLoadingStateFromQueryStatus } from 'modules/Monitoring/utils/deriveLoadingStateFromQueryStatus'

import { getColumns } from './utils/getColumns'
import { getSpecialisationReport } from './utils/getSpecialisationReport'
import { prepareTableData } from './utils/prepareTableData'

type QueuesHealthDetailsTableProps = {
  data: SpecialisationHealth[]
  queryStatus: QueryStatus
  metrics?: QueueHealthMetricsOverview
  resourcesSideParams?: ResourcesSideParams
  selectedIncidentEntity?: IncidentTimelineSearchRequest
  onShowResources: (value: ResourcesSideParams | undefined) => void
}

export const QueuesHealthDetailsTable: FC<QueuesHealthDetailsTableProps> = ({
  data,
  metrics,
  queryStatus,
  resourcesSideParams,
  onShowResources,
}) => {
  const { entityName, isDante } = useChannel()
  const { addSuccessToast } = useToasts()

  const { state: tableState, setSorting } = useTableState(
    UrlStateStorageKeys.QueueHealthDetailsTableState,
  )

  const specialisationLabel = isDante ? 'Ownership & Queues' : 'Specialisation'
  const preparedData = useMemo(() => prepareTableData(data), [data])

  const columns = useMemo(
    () =>
      getColumns({
        resourcesSideParams,
        entityName,
        specialisationLabel,
        metrics,
        isDante,
        onShowResources,
        onCopyReport: async row => {
          await copyToClipboard(getSpecialisationReport(row, entityName, isDante))

          addSuccessToast(`Report for ${row.name} copied`)
        },
      }),
    [
      resourcesSideParams,
      entityName,
      isDante,
      specialisationLabel,
      metrics,
      addSuccessToast,
      onShowResources,
    ],
  )

  return (
    <LimitedTable
      state={tableState}
      columns={columns}
      data={preparedData}
      getRowId={row => row.id}
      rowsToShow={12}
      aria-label="Queue health table"
      primaryColumnId="name"
      loadingState={deriveLoadingStateFromQueryStatus(queryStatus)}
      expandable
      resizable
      showingCount
      enableMultiSort
      onSortingChange={setSorting}
    />
  )
}
